import { useCallback, useState } from "react";
import HeroSlider from "./HeroSlider";
import HeroVideo from "./HeroVideo";
import CyberSecWeek3Slider from "./CyberSecWeek3Slider";
import "./Hero1.css";
import HeroFilters from "./Herofilter";

// Expiry mirrors CyberSecWeek3Slider — expires 31 Oct 2026.
// When expired Hero.jsx skips straight to the main slider.
const CYBER_EXPIRY  = new Date("2026-10-31T23:59:59");
const cyberActive   = () => Date.now() <= CYBER_EXPIRY.getTime();

// Hero reel (when cyber campaign is live):
//   video → cyber-slider → main-slider → video → …
//
// After 31 Oct 2026:
//   video → main-slider → video → …
//
// stage values:
//   "video"        — HeroVideo
//   "cyber"        — CyberSecWeek3Slider  (5 educational slides)
//   "slides"       — HeroSlider      (main promo carousel)

export default function Hero() {
    const [stage,      setStage]      = useState("video");
    const [entrySlide, setEntrySlide] = useState(0);

    // Remount keys so each component restarts cleanly on re-entry
    const [videoRun,   setVideoRun]   = useState(0);
    const [cyberRun,   setCyberRun]   = useState(0);
    const [sliderRun,  setSliderRun]  = useState(0);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const openCyber = useCallback(() => {
        setCyberRun((n) => n + 1);
        setStage("cyber");
    }, []);

    const openSlider = useCallback((index) => {
        setEntrySlide(index);
        setSliderRun((n) => n + 1);
        setStage("slides");
    }, []);

    const backToVideo = useCallback(() => {
        setVideoRun((n) => n + 1);
        setStage("video");
    }, []);

    // ── Video callbacks ───────────────────────────────────────────────────────
    const handleVideoEnd = useCallback(() => {
        if (cyberActive()) { openCyber(); } else { openSlider(0); }
    }, [openCyber, openSlider]);

    const handleVideoNext = useCallback(() => {
        if (cyberActive()) { openCyber(); } else { openSlider(0); }
    }, [openCyber, openSlider]);

    // Stepping back from video → last main slide
    const handleVideoPrev = useCallback(() => {
        openSlider(-1);
    }, [openSlider]);

    // ── CyberSec slider callbacks ─────────────────────────────────────────────
    // Finished all 5 slides → main slider
    const handleCyberEnd = useCallback(() => {
        openSlider(0);
    }, [openSlider]);

    // Arrow-left off slide 1 → back to video
    const handleCyberRequestVideo = useCallback(() => {
        backToVideo();
    }, [backToVideo]);

    // ── Render ────────────────────────────────────────────────────────────────
    const isVideo  = stage === "video";
    const isCyber  = stage === "cyber";
    const isSlider = stage === "slides";

    return (
        <>
            <section className={`hero ${isVideo ? "hero--video" : "hero--slider"}`}>

                {isVideo && (
                    <HeroVideo
                        key={videoRun}
                        onVideoEnd={handleVideoEnd}
                        onNext={handleVideoNext}
                        onPrev={handleVideoPrev}
                    />
                )}

                {isCyber && (
                    <CyberSecWeek3Slider
                        key={cyberRun}
                        onCycleEnd={handleCyberEnd}
                        onRequestVideo={handleCyberRequestVideo}
                    />
                )}

                {isSlider && (
                    <HeroSlider
                        key={sliderRun}
                        initialIndex={entrySlide}
                        onCycleEnd={backToVideo}
                        onRequestVideo={backToVideo}
                    />
                )}

                <div className="overlay"></div>
            </section>

            <HeroFilters />
        </>
    );
}
